import React from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { CheckCircle, Clock, Users, BarChart3, Shield, Zap, Star, ArrowRight, ShoppingBag } from 'lucide-react';

const TimeTrackingSoftware = () => {
  const handleGetStarted = () => {
    window.location.href = '/manager-dashboard';
  };

  const handleProTrial = () => {
    window.open('https://buy.stripe.com/5kQ9ATgrw2EWb1nci42ZO0a', '_blank');
  };

  const competitors = [
    { name: "Enterprise Solutions", weakness: "Complex setup, high implementation costs" },
    { name: "Traditional Systems", weakness: "Limited mobile functionality" },
    { name: "Legacy Platforms", weakness: "Higher monthly fees" },
    { name: "Basic HR Tools", weakness: "Limited reporting features" },
    { name: "Standard Software", weakness: "Basic analytics" }
  ];

  const features = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Real-Time Time Tracking",
      description: "Instant clock in/out with PIN security. Track employee hours across multiple locations with live dashboard updates."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Multi-Location Workforce Management",
      description: "Manage unlimited employees across multiple sites. Perfect for retail chains, restaurants, and distributed teams."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Advanced Analytics & Reporting",
      description: "AI-powered insights, automated timesheets, and comprehensive reports. Export to CSV for payroll integration."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security Features",
      description: "PIN-based authentication helps prevent unauthorized time entries. Includes additional security measures for business use."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "5-Minute Setup",
      description: "Quick and easy deployment with minimal configuration required. Get your team tracking time in minutes, not weeks."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/tyme-logo-new.png" alt="Tyme Time Tracking Software" className="h-12" />
          </a>
          <Button onClick={handleGetStarted} className="bg-gradient-to-r from-indigo-600 to-purple-600">
            Start Free Trial
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Professional <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Time Tracking Software</span> for UK Businesses
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional employee time tracking and attendance management software with AI analytics, payroll integration, and multi-location support. Designed for UK businesses seeking efficient workforce management solutions.
            </p>
            
            {/* Key Benefits */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">Why UK Businesses Choose Tyme</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Quick 5-minute setup process</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Affordable pricing for all businesses</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Comprehensive mobile application</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={handleProTrial}
                size="lg"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold"
              >
                Start 3-Day Pro Trial - Setup in 5 Minutes
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Comparison Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-16">
            Tyme Features Overview
          </h2>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="text-center p-6">
                <CardHeader>
                  <CardTitle className="text-lg text-indigo-600">Setup Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 mb-2">5 Minutes</div>
                  <p className="text-sm text-slate-600">Quick and easy deployment</p>
                </CardContent>
              </Card>
              
              <Card className="text-center p-6">
                <CardHeader>
                  <CardTitle className="text-lg text-indigo-600">Pricing</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 mb-2">From £19</div>
                  <p className="text-sm text-slate-600">Affordable for all business sizes</p>
                </CardContent>
              </Card>
              
              <Card className="text-center p-6">
                <CardHeader>
                  <CardTitle className="text-lg text-indigo-600">AI Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600 mb-2">✓ Yes</div>
                  <p className="text-sm text-slate-600">Advanced reporting and insights</p>
                </CardContent>
              </Card>
              
              <Card className="text-center p-6">
                <CardHeader>
                  <CardTitle className="text-lg text-indigo-600">Mobile App</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600 mb-2">✓ Yes</div>
                  <p className="text-sm text-slate-600">Comprehensive mobile solution</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-16">
            Complete Time Tracking & Attendance Management Features
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center text-indigo-600 mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-Specific Solutions */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Industry-Specific Time Tracking Solutions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Small Business Focus
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  Discover why UK small businesses choose Tyme over expensive enterprise solutions. Perfect for 5-50 employees.
                </p>
                <a 
                  href="/small-business-time-clock" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  Explore Small Business Solutions
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-indigo-600" />
                  Retail Specialists
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  Multi-store retail chains need specialized features. Learn about our retail-specific attendance management.
                </p>
                <a 
                  href="/retail-attendance-software" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  View Retail-Specific Features
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">
              Ready to see all features and get started?
            </p>
            <a 
              href="/" 
              className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2"
            >
              ← View Complete Feature Overview
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Switch from Your Current Time Tracking Software?
          </h2>
          <p className="text-xl mb-8 text-indigo-100 max-w-2xl mx-auto">
            Join UK businesses using Tyme for efficient time tracking and workforce management. 
            Setup takes 5 minutes and migration support is included. Try Pro features free for 3 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleProTrial}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-slate-50 px-8 py-4 text-lg font-semibold"
            >
              Start 3-Day Pro Trial Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <img src="/tyme-logo-new.png" alt="Tyme" className="h-12 mx-auto mb-6" />
          <p className="text-slate-400 mb-4">Professional time tracking and attendance management software</p>
          <p className="text-slate-500 text-sm">
            Comprehensive workforce management solution for UK businesses
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TimeTrackingSoftware;